/**
 * MentionInput Component
 * Rich text input with @mention autocomplete and styled mentions
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { User, Loader2 } from 'lucide-react'
import { getMentionableUsers } from '../services/notifications'
import type { MentionableUser } from '../types/notification'

interface MentionInputProps {
  value: string
  onChange: (value: string) => void
  onMentionsChange: (userIds: string[]) => void
  placeholder?: string
  rows?: number
  disabled?: boolean
  className?: string
  assignmentId?: string  // For TGs: required to see colleagues on same assignment
}

// Convert plain text with @"Name" to HTML with styled mentions
function textToHtml(text: string): string {
  const mentionRegex = /@"([^"]+)"|@(\w+)/g
  return text.replace(mentionRegex, (match, quotedName, simpleName) => {
    const name = quotedName || simpleName
    // Escape quotes in data-mention attribute to prevent HTML breakage
    const escapedMatch = match.replace(/"/g, '&quot;')
    return `<span class="mention" contenteditable="false" data-mention="${escapedMatch}">@${name}</span>`
  })
}

// Convert HTML back to plain text with @"Name" format
function htmlToText(html: string): string {
  const div = document.createElement('div')
  div.innerHTML = html

  // Replace mention spans with their data-mention value
  div.querySelectorAll('.mention').forEach(span => {
    const textNode = document.createTextNode(span.getAttribute('data-mention') || '')
    span.replaceWith(textNode)
  })

  // Get text content, preserving line breaks
  return div.innerText || div.textContent || ''
}

export function MentionInput({
  value,
  onChange,
  onMentionsChange,
  placeholder = 'Skriv en kommentar... Använd @ för att nämna någon',
  rows = 3,
  disabled = false,
  className = '',
  assignmentId
}: MentionInputProps) {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mentionedUsers, setMentionedUsers] = useState<Map<string, MentionableUser>>(new Map())
  const [mentionStartOffset, setMentionStartOffset] = useState(-1)

  const editorRef = useRef<HTMLDivElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const isComposing = useRef(false)

  // Fetch users for autocomplete
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['mentionable-users', searchTerm, assignmentId],
    queryFn: () => getMentionableUsers(searchTerm, assignmentId),
    enabled: showSuggestions && searchTerm.length >= 0,
    staleTime: 30000
  })

  // Update parent when mentioned users change
  useEffect(() => {
    onMentionsChange(Array.from(mentionedUsers.keys()))
  }, [mentionedUsers, onMentionsChange])

  // Initialize editor content and clear mentions when value is emptied
  useEffect(() => {
    if (editorRef.current && value !== htmlToText(editorRef.current.innerHTML)) {
      editorRef.current.innerHTML = textToHtml(value) || '<br>'
    }
    // Clear mentioned users when value is empty (e.g., after form submit)
    if (!value || value.trim() === '') {
      setMentionedUsers(new Map())
    }
  }, [value])

  // Get current cursor position info
  const getCursorInfo = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || !selection.rangeCount) return null

    const range = selection.getRangeAt(0)
    const preCaretRange = range.cloneRange()
    preCaretRange.selectNodeContents(editorRef.current!)
    preCaretRange.setEnd(range.endContainer, range.endOffset)

    // Get the visible text before cursor (for @ detection)
    const visibleTextBeforeCursor = preCaretRange.toString()

    // Also calculate raw text offset by converting HTML up to cursor
    // We need to build the raw text by walking through nodes
    let rawTextBeforeCursor = ''
    const tempDiv = document.createElement('div')
    const fragment = preCaretRange.cloneContents()
    tempDiv.appendChild(fragment)

    // Replace mention spans with their data-mention value
    tempDiv.querySelectorAll('.mention').forEach(span => {
      const textNode = document.createTextNode(span.getAttribute('data-mention') || '')
      span.replaceWith(textNode)
    })
    rawTextBeforeCursor = tempDiv.innerText || tempDiv.textContent || ''

    return {
      offset: rawTextBeforeCursor.length,
      range,
      textBeforeCursor: visibleTextBeforeCursor,
      rawTextBeforeCursor
    }
  }, [])

  // Handle input changes
  const handleInput = useCallback(() => {
    if (isComposing.current) return
    if (!editorRef.current) return

    const newText = htmlToText(editorRef.current.innerHTML)
    onChange(newText)

    // Check for @ mention
    const cursorInfo = getCursorInfo()
    if (!cursorInfo) return

    // Use raw text (with quotes) for offset calculation
    const rawTextBeforeCursor = cursorInfo.rawTextBeforeCursor
    const lastAtIndex = rawTextBeforeCursor.lastIndexOf('@')

    if (lastAtIndex !== -1) {
      const textAfterAt = rawTextBeforeCursor.slice(lastAtIndex + 1)

      // Check if this is a completed quoted mention (has opening and closing quotes with space after)
      // Pattern: @"Name" followed by space means mention is complete
      if (textAfterAt.startsWith('"')) {
        const closingQuoteIndex = textAfterAt.indexOf('" ', 1)
        if (closingQuoteIndex !== -1) {
          // Mention is complete, don't show suggestions
          setShowSuggestions(false)
          setMentionStartOffset(-1)
          return
        }
        // Incomplete quoted mention - show suggestions
        setMentionStartOffset(lastAtIndex)
        setSearchTerm(textAfterAt.slice(1)) // Remove opening quote
        setShowSuggestions(true)
        setSelectedIndex(0)
        return
      }

      // Simple mention (no quotes) - valid if no space after @
      if (!textAfterAt.includes(' ')) {
        setMentionStartOffset(lastAtIndex)
        setSearchTerm(textAfterAt)
        setShowSuggestions(true)
        setSelectedIndex(0)
        return
      }
    }

    setShowSuggestions(false)
    setMentionStartOffset(-1)
  }, [onChange, getCursorInfo])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !showSuggestions) {
      // Allow Ctrl+Enter to submit (handled by parent)
      if (e.ctrlKey) return
    }

    if (!showSuggestions || users.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, users.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
      case 'Tab':
        if (users[selectedIndex]) {
          e.preventDefault()
          selectUser(users[selectedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setShowSuggestions(false)
        break
    }
  }

  // Select a user from suggestions
  const selectUser = useCallback((user: MentionableUser) => {
    if (!editorRef.current) return

    // Get current text and cursor position
    const currentText = htmlToText(editorRef.current.innerHTML)
    const cursorInfo = getCursorInfo()
    if (!cursorInfo || mentionStartOffset === -1) return

    const beforeMention = currentText.slice(0, mentionStartOffset)
    const afterCursor = currentText.slice(cursorInfo.offset)

    // Format mention
    const hasSpace = user.full_name.includes(' ')
    const mentionText = hasSpace ? `@"${user.full_name}" ` : `@${user.full_name} `

    const newText = beforeMention + mentionText + afterCursor

    // Update the editor with styled content
    editorRef.current.innerHTML = textToHtml(newText) || '<br>'
    onChange(newText)

    // Add to mentioned users
    setMentionedUsers(prev => new Map(prev).set(user.user_id, user))

    // Reset mention state
    setShowSuggestions(false)
    setMentionStartOffset(-1)
    setSearchTerm('')

    // Set cursor position after the mention
    setTimeout(() => {
      if (!editorRef.current) return
      editorRef.current.focus()

      // Find the position right after the inserted mention
      const targetPosition = beforeMention.length + mentionText.length

      // Walk through text nodes to find the right position
      const walker = document.createTreeWalker(
        editorRef.current,
        NodeFilter.SHOW_TEXT,
        null
      )

      let currentPos = 0
      let targetNode: Text | null = null
      let targetOffset = 0

      while (walker.nextNode()) {
        const node = walker.currentNode as Text
        const nodeLength = node.textContent?.length || 0

        if (currentPos + nodeLength >= targetPosition) {
          targetNode = node
          targetOffset = targetPosition - currentPos
          break
        }
        currentPos += nodeLength
      }

      const range = document.createRange()
      const selection = window.getSelection()

      if (targetNode) {
        range.setStart(targetNode, Math.min(targetOffset, targetNode.length))
        range.collapse(true)
      } else {
        // Fallback: move to end
        range.selectNodeContents(editorRef.current)
        range.collapse(false)
      }

      selection?.removeAllRanges()
      selection?.addRange(range)
    }, 0)
  }, [mentionStartOffset, getCursorInfo, onChange])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        editorRef.current &&
        !editorRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Scroll selected item into view
  useEffect(() => {
    if (showSuggestions && suggestionsRef.current) {
      const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex, showSuggestions])

  const getRoleLabel = (role: string) => {
    return role === 'admin' ? 'Admin' : 'Trädgårdsmästare'
  }

  const minHeight = rows * 24 + 24 // Approximate line height + padding

  return (
    <div className="relative">
      {/* Styled contenteditable editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => { isComposing.current = true }}
        onCompositionEnd={() => { isComposing.current = false; handleInput() }}
        data-placeholder={placeholder}
        className={`
          w-full p-3 rounded-lg border border-nature-300 dark:border-nature-600
          bg-nature-50 dark:bg-nature-900 text-nature-900 dark:text-nature-100
          focus:ring-2 focus:ring-primary-500 focus:border-primary-500
          outline-none overflow-y-auto whitespace-pre-wrap
          empty:before:content-[attr(data-placeholder)] empty:before:text-nature-400
          [&_.mention]:text-primary-600 [&_.mention]:dark:text-primary-400 [&_.mention]:font-medium
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
        style={{ minHeight: `${minHeight}px` }}
      />

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-48 overflow-y-auto"
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-3">
              <Loader2 className="h-4 w-4 animate-spin text-nature-500" />
              <span className="ml-2 text-sm text-nature-500">Söker...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="p-3 text-sm text-nature-500 text-center">
              Inga användare hittades
            </div>
          ) : (
            users.map((user, index) => (
              <button
                key={user.user_id}
                onClick={() => selectUser(user)}
                className={`
                  w-full flex items-center gap-3 p-3 text-left transition-colors
                  ${index === selectedIndex
                    ? 'bg-primary-50 dark:bg-primary-900/30'
                    : 'hover:bg-nature-50 dark:hover:bg-nature-700'
                  }
                `}
              >
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${user.role === 'admin'
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400'
                    : 'bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400'
                  }
                `}>
                  <User className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-nature-900 dark:text-nature-100 truncate">
                    {user.full_name}
                  </p>
                  <p className="text-xs text-nature-500 truncate">
                    {getRoleLabel(user.role)} • {user.email}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Mentioned users badges */}
      {mentionedUsers.size > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {Array.from(mentionedUsers.values()).map(user => (
            <span
              key={user.user_id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full"
            >
              @{user.full_name}
              <button
                onClick={() => {
                  setMentionedUsers(prev => {
                    const next = new Map(prev)
                    next.delete(user.user_id)
                    return next
                  })
                }}
                className="hover:text-primary-900 dark:hover:text-primary-100"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
